import axios from 'axios';
import { 
  LoginCredentials, 
  AuthResponse, 
  SensorResponse, 
  SensorData,
  MaintenanceTemplate,
  MaintenanceTemplateTask,
  MaintenanceSchedule,
  MaintenanceRecord,
  MaintenanceStatus
} from '../types';

// Configure axios defaults
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.log('API Error Response:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      data: error.response?.data,
      pathname: window.location.pathname
    });
    
    if (error.response?.status === 401) {
      console.warn('401 Unauthorized - clearing auth data');
      // Clear auth data but don't redirect - let React handle it
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      // Dispatch a custom event to notify the auth context
      window.dispatchEvent(new CustomEvent('auth-logout'));
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const response = await api.post('/login', credentials);
    return response.data;
  },

  logout: async (): Promise<void> => {
    await api.post('/logout');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
  },

  getUser: async () => {
    const response = await api.get('/user');
    return response.data;
  },

  validateToken: async (): Promise<boolean> => {
    try {
      const response = await api.get('/validate-token');
      return response.data.valid === true;
    } catch (error) {
      console.error('Token validation failed:', error);
      return false;
    }
  },
};

export const sensorAPI = {
  getAllSensors: async (): Promise<SensorResponse> => {
    const response = await api.get('/sensors');
    return response.data;
  },

  getSensor: async (id: number): Promise<{ sensor: SensorData }> => {
    const response = await api.get(`/sensors/${id}`);
    return response.data;
  },

  getSensorHistory: async (
    id: number, 
    period?: 'today' | 'yesterday' | 'week' | 'month',
    fromDate?: string,
    toDate?: string,
    singleDate?: string
  ) => {
    const params: any = {};
    if (period) params.period = period;
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    if (singleDate) params.single_date = singleDate;
    
    const response = await api.get(`/sensors/${id}/history`, { params });
    return response.data;
  },

  getFuelDecreaseThresholds: async (sensorId: number) => {
    const response = await api.get(`/sensors/${sensorId}/thresholds`);
    return response.data;
  },

  setFuelDecreaseThresholds: async (sensorId: number, thresholds: any) => {
    const response = await api.post(`/sensors/${sensorId}/thresholds`, thresholds);
    return response.data;
  },

  deleteFuelDecreaseThresholds: async (sensorId: number) => {
    const response = await api.delete(`/sensors/${sensorId}/thresholds`);
    return response.data;
  },

  exportSensorHistory: async (id: number, period: 'today' | 'yesterday' | 'week' | 'month') => {
    const response = await api.get(`/sensors/${id}/history/export`, {
      params: { period },
      responseType: 'blob' // Important for file downloads
    });
    
    // Create a blob URL and trigger download
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Extract filename from response headers or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = `sensor-history-${period}-${new Date().toISOString().split('T')[0]}.xlsx`;
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return { success: true, filename };
  },

  getGenerators: async () => {
    const response = await api.get('/generators');
    return response.data;
  },

  generateGeneratorReport: async (generatorId: number, params: any) => {
    const response = await api.get(`/generators/${generatorId}/report`, { params });
    return response.data;
  }
};

export const maintenanceAPI = {
  // Templates
  getTemplates: async (params?: { is_active?: boolean; service_type?: string }): Promise<{ templates: MaintenanceTemplate[] }> => {
    const response = await api.get('/maintenance-templates', { params });
    return response.data;
  },

  getTemplate: async (id: number): Promise<{ template: MaintenanceTemplate }> => {
    const response = await api.get(`/maintenance-templates/${id}`);
    return response.data;
  },

  createTemplate: async (data: Partial<MaintenanceTemplate>): Promise<{ template: MaintenanceTemplate }> => {
    const response = await api.post('/maintenance-templates', data);
    return response.data;
  },

  updateTemplate: async (id: number, data: Partial<MaintenanceTemplate>): Promise<{ template: MaintenanceTemplate }> => {
    const response = await api.put(`/maintenance-templates/${id}`, data);
    return response.data;
  },

  deleteTemplate: async (id: number): Promise<void> => {
    await api.delete(`/maintenance-templates/${id}`);
  },

  // Schedules
  getSchedules: async (params?: { status?: string; is_overdue?: boolean; generator_id?: number }): Promise<{ schedules: MaintenanceSchedule[] }> => {
    const response = await api.get('/maintenance-schedules', { params });
    return response.data;
  },

  getSchedule: async (id: number): Promise<{ schedule: MaintenanceSchedule }> => {
    const response = await api.get(`/maintenance-schedules/${id}`);
    return response.data;
  },

  createSchedule: async (data: Partial<MaintenanceSchedule>): Promise<{ schedule: MaintenanceSchedule }> => {
    const response = await api.post('/maintenance-schedules', data);
    return response.data;
  },

  updateSchedule: async (id: number, data: Partial<MaintenanceSchedule>): Promise<{ schedule: MaintenanceSchedule }> => {
    const response = await api.put(`/maintenance-schedules/${id}`, data);
    return response.data;
  },

  deleteSchedule: async (id: number): Promise<void> => {
    await api.delete(`/maintenance-schedules/${id}`);
  },

  refreshSchedule: async (id: number): Promise<{ schedule: MaintenanceSchedule }> => {
    const response = await api.post(`/maintenance-schedules/${id}/refresh`);
    return response.data;
  },

  getGeneratorStatus: async (generatorId: number): Promise<{ status: MaintenanceStatus }> => {
    const response = await api.get(`/generators/${generatorId}/maintenance-status`);
    return response.data;
  },

  // Records
  getRecords: async (params?: { 
    schedule_id?: number; 
    generator_id?: number; 
    from_date?: string; 
    to_date?: string; 
    service_status?: string;
    per_page?: number;
  }): Promise<{ records: { data: MaintenanceRecord[]; current_page: number; total: number } }> => {
    const response = await api.get('/maintenance-records', { params });
    return response.data;
  },

  getRecord: async (id: number): Promise<{ record: MaintenanceRecord }> => {
    const response = await api.get(`/maintenance-records/${id}`);
    return response.data;
  },

  createRecord: async (data: Partial<MaintenanceRecord>): Promise<{ record: MaintenanceRecord }> => {
    const response = await api.post('/maintenance-records', data);
    return response.data;
  },

  updateRecord: async (id: number, data: Partial<MaintenanceRecord>): Promise<{ record: MaintenanceRecord }> => {
    const response = await api.put(`/maintenance-records/${id}`, data);
    return response.data;
  },

  deleteRecord: async (id: number): Promise<void> => {
    await api.delete(`/maintenance-records/${id}`);
  },
};

export default api;